/**
 * O editor gráfico de um componente do diagrama.
 *
 * **O desenho de UM componente custa 2,5 s; o do diagrama, 10,4 s.** A prévia do
 * diagrama inteiro (7141, 129 componentes) não serve para acompanhar um campo de
 * altura sendo digitado. A rota nova desenha só este componente — e por dentro
 * ela usa o mesmo caminho que a produção usa (carga única do diagrama), porque o
 * caminho "um componente pede as próprias ligações" chegou a 22 s em alguns.
 *
 * **A unidade é o passo**, 1,5 mm. É nela que altura e deslocamento são
 * digitados; o px vem junto só para conferir com o olho o que o traço mostra.
 *
 * **`PUT ligacoes/{id}` é substituição completa**, como o dos terminais: quem não
 * lê `ramificacaoId`, `textoDescricao` e os dois campos da junção grava zero por
 * cima deles. Por isso `GET componentes/{id}/terminais` passou a devolver os
 * quatro — sem eles, mexer numa altura desfazia a junção em silêncio.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, noProjeto, obter } from "./cliente"

/** A altura de um terminal nas duas unidades em que o desenho pensa. */
export type MedidaDoTerminal = {
  diagramaLigacaoId: number
  pino: string
  posicao: string
  passos: number
  px: number
}

export type DesenhoDoComponente = {
  diagramaComponenteId: number
  diagramaId: number
  titulo: string
  componenteCodigo: string
  componenteDescricao: string
  posicao: string
  /** Documento SVG completo — vai direto num `srcDoc`/`<img>` ou embutido. */
  svg: string
  passoPx: number
  passoMm: number
  larguraPassos: number
  larguraPx: number
  alturaPassos: number
  alturaPx: number
  /**
   * O tamanho da TELA do desenho, que não é o do componente: o motor desenha
   * numa faixa de 68 passos (385 px) porque ali cabem o componente, a ligação e
   * a coluna de texto. É esta a largura que o visor usa para escalar.
   */
  svgLarguraPx: number
  svgAlturaPx: number
  terminais: MedidaDoTerminal[]
  milissegundosBusca: number
  milissegundosDesenho: number
  milissegundosEstilo: number
}

/**
 * `staleTime: 0` de propósito: o traço é o retrato de agora, e depois de gravar
 * uma altura o que se quer ver é o desenho novo, não o de dez segundos atrás.
 */
export function usarDesenhoDoComponente(diagramaComponenteId: number, grid = true) {
  return useQuery({
    queryKey: ["componente-desenho", diagramaComponenteId, grid] as const,
    enabled: diagramaComponenteId > 0,
    staleTime: 0,
    queryFn: () => obter<DesenhoDoComponente>(
      `api/diagramas/componentes/${diagramaComponenteId}/desenho`, { grid }),
  })
}

/** Um terminal que este pode tomar como ramificação ou como junção. */
export type CandidatoDeLigacao = {
  diagramaLigacaoId: number
  diagramaComponenteId: number
  pinoutId: number
  componenteCodigo: string
  componenteDescricao: string
  pino: string
  circuitoDescricao: string
  pinoDaLigacao: string
  /** Os cinco seguintes dizem ONDE o terminal está — sem eles, dez "31 Aterramento" são iguais. */
  diagramaComponenteTitulo?: string
  /** `left` / `right` — o lado do diagrama em que o componente está. */
  posicao?: string
  /** O lugar do componente na coluna desse lado, de cima para baixo. */
  ordem?: number
  /** O componente do outro lado da ligação. */
  outroCodigo?: string
  outroDescricao?: string
}

const LADOS: Record<string, string> = { left: "esquerda", right: "direita" }

/** O componente a que o terminal pertence — o rótulo do `optgroup`. */
export function nomeDoComponenteDoCandidato(k: CandidatoDeLigacao): string {
  return [k.componenteCodigo, k.componenteDescricao].filter(Boolean).join(" ") || "Sem componente"
}

/** Terminais que o operador não distingue pelo texto — mesmo componente, pino e circuito. */
export function chaveDoGrupoDeCandidatos(k: CandidatoDeLigacao): string {
  return [nomeDoComponenteDoCandidato(k), k.pino, k.circuitoDescricao].join("|")
}

export type GrupoDeCandidatos = {
  chave: string
  componente: string
  /** `pino 1 · 31.A` — o que a pessoa lê no 1º select. */
  rotulo: string
  itens: CandidatoDeLigacao[]
}

/** Agrupa na ordem em que a API devolveu (componente, pino). */
export function agruparCandidatos(lista: CandidatoDeLigacao[]): GrupoDeCandidatos[] {
  const grupos = new Map<string, GrupoDeCandidatos>()
  for (const k of lista) {
    const chave = chaveDoGrupoDeCandidatos(k)
    let g = grupos.get(chave)
    if (!g) {
      g = {
        chave, componente: nomeDoComponenteDoCandidato(k), itens: [],
        rotulo: [`pino ${k.pino || "—"}`, k.circuitoDescricao].filter(Boolean).join(" · "),
      }
      grupos.set(chave, g)
    }
    g.itens.push(k)
  }
  return [...grupos.values()]
}

/** `pino 1 · 31.A (3 posições)` — a contagem só aparece quando há repetidos. */
export function rotuloDoGrupo(g: GrupoDeCandidatos): string {
  return g.itens.length > 1 ? `${g.rotulo} (${g.itens.length} posições)` : g.rotulo
}

/** O 2º passo: onde o terminal está no diagrama e para onde liga. */
export function rotuloDaPosicao(k: CandidatoDeLigacao): string {
  const lado = k.posicao ? (LADOS[k.posicao] ?? k.posicao) : ""
  const onde = lado ? `${lado}${k.ordem ? ` nº ${k.ordem}` : ""}` : ""
  const titulo = k.diagramaComponenteTitulo?.trim()
  const outro = [k.outroCodigo, k.outroDescricao].filter(Boolean).join(" ")
  const texto = [
    onde,
    titulo && `“${titulo}”`,
    outro && `liga em ${outro}${k.pinoDaLigacao ? ` (pino ${k.pinoDaLigacao})` : ""}`,
  ].filter(Boolean).join(" · ")
  return texto || `terminal ${k.diagramaLigacaoId}`
}

/**
 * A mesma lista serve aos dois campos — no legado são dois botões, "Carregar
 * ramificação" e "Carregar junção", que chamam a mesma consulta. Só carrega
 * quando o campo pede, que é o que o legado faz: são todos os terminais do
 * diagrama no mesmo circuito, e num diagrama grande isso não é lista de abrir à
 * toa.
 */
export function usarCandidatosDaLigacao(diagramaLigacaoId: number, ligado: boolean) {
  return useQuery({
    queryKey: ["ligacao-candidatos", diagramaLigacaoId] as const,
    enabled: ligado && diagramaLigacaoId > 0,
    staleTime: 30_000,
    queryFn: () => obter<CandidatoDeLigacao[]>(
      `api/diagramas/ligacoes/${diagramaLigacaoId}/candidatos`, {}),
  })
}

/** O corpo inteiro do `DiagramaLigacaoFinalizar`. */
export type LigacaoParaGravar = {
  altura: number
  deslocar: boolean
  ramificacao: number
  ramificacaoId: number
  ramificacaoPosicao: number
  ocultar: boolean
  juncao: boolean
  juncaoDiagramaComponenteId: number
  juncaoPinoutId: number
  texto: string
  textoDescricao: string
}

/**
 * Grava a ligação e derruba o desenho do componente e o dos vizinhos que a
 * resposta apontar — uma junção mexe nos dois lados, e mostrar só um deles
 * atualizado seria pior do que não mostrar nenhum.
 */
export function usarEditarLigacao(projetoId: number, diagramaId: number, componenteId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, corpo }: { id: number; corpo: LigacaoParaGravar }) => {
      const r = await api.put<{ ok: boolean; componentesParaAtualizar: number[] }>(
        `api/diagramas/ligacoes/${id}`, corpo, noProjeto(projetoId))
      return r.data
    },
    onSuccess: (r) => {
      for (const outro of [componenteId, ...(r?.componentesParaAtualizar ?? [])])
        qc.invalidateQueries({ queryKey: ["componente-desenho", outro] })
      qc.invalidateQueries({ queryKey: ["componente-terminais", componenteId] })
      qc.invalidateQueries({ queryKey: ["diagrama-componentes", diagramaId] })
      qc.invalidateQueries({ queryKey: ["diagrama-desenho-estado", diagramaId] })
      qc.invalidateQueries({ queryKey: ["diagramas", projetoId] })
    },
  })
}

/** `ramificacao` — de onde o traço sai. Os nomes são os do select do legado. */
export const RAMIFICACOES: ReadonlyArray<readonly [number, string]> = [
  [0, "Não ramifica"],
  [1, "A partir do componente principal"],
  [2, "A partir do componente secundário"],
]
